import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { RosterMemberService } from '../instances/roster-member';
import { RosterMemberConverter } from '@/converters';
import { RosterReorderRequest } from '../request/roster-member';

export const useRosterMember = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_ROSTER_MEMBER', params],
    async () => {
      const [err, data] = await RosterMemberService.getRosterMembers(params);
      if (err) {
        throw err;
      }
      return {
        data: RosterMemberConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useRosterMemberById = (id: string) => {
  const query = useQuery(
    ['GET_ROSTER_MEMBER_BY_ID', id],
    async () => {
      const [err, data] = await RosterMemberService.getRosterMemberById(id);
      if (err) {
        throw err;
      }
      return RosterMemberConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetRosterMemberById = () => {
  return useMutation({
    mutationKey: ['GET_ROSTER_MEMBER_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await RosterMemberService.getRosterMemberById(uuid);
      if (err) {
        throw err;
      }
      return RosterMemberConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostRosterMemberMutation = () => {
  return useMutation({
    mutationKey: ['POST_ROSTER_MEMBER'],
    mutationFn: async (payload: any) => {
      const [err, data] = await RosterMemberService.postRosterMember(payload);
      if (err) {
        throw err;
      }
      return RosterMemberConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutRosterMemberMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ROSTER_MEMBER'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await RosterMemberService.putRosterMember(id, payload);
      if (err) {
        throw err;
      }
      return RosterMemberConverter.toDomainFromPutResponse(data);
    },
  });
};

export const useDeleteRosterMemberMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_ROSTER_MEMBER'],
    mutationFn: async (id: string) => {
      const [err] = await RosterMemberService.deleteRosterMember(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};

export const usePutRosterMemberStatisticsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_ROSTER_MEMBER_STATISTICS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await RosterMemberService.putRosterMemberStatistics(id, payload);
      if (err) {
        throw err;
      }
      return RosterMemberConverter.toDomainFromPutStatisticsResponse(data);
    },
  });
};

export const useReorderRosterMembersMutation = () => {
  return useMutation({
    mutationKey: ['REORDER_ROSTER_MEMBERS'],
    mutationFn: async (payload: RosterReorderRequest) => {
      const [err] = await RosterMemberService.reorderRosterMembers(payload);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
